'use client';

import { ArrowUpRight } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { LinkPreview } from '@/components/ui/link-preview';
import { useTranslation } from '@/lib/i18n/context';

const InsightsCapabilities = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t.features_pages['Identifies Root Causes'],
      description:
        t.features_pages[
          "AI agent understands what's actually driving your support volume. Not just symptoms—root causes. Is it a bug? Missing feature? Confusing UX? Poor documentation?"
        ],
    },
    {
      id: 2,
      title: t.features_pages['Clusters Similar Issues'],
      description:
        t.features_pages[
          'Groups similar support requests, feature asks, and bug reports together. 100 conversations about the same issue become one cluster with full context and customer count.'
        ],
    },
    {
      id: 3,
      title: t.features_pages['Generates Jira Tickets'],
      description:
        t.features_pages[
          'Deep research agent creates complete Jira tickets automatically. Title, description, user impact analysis, affected customer count, reproduction steps, and priority—all generated from clustered conversations.'
        ],
    },
    {
      id: 4,
      title: t.features_pages['Cross-Channel Analysis'],
      description:
        t.features_pages[
          'Analyzes support requests from Voice, Chat, Email, WhatsApp, Social, and Slack. See the same issue reported across different channels, clustered together.'
        ],
    },
    {
      id: 5,
      title: t.features_pages['Team Gamification'],
      description:
        t.features_pages[
          "Teams compete on leaderboards to resolve the most impactful insights. Track who's fixing the issues that affect the most customers. Turn problem-solving into motivation."
        ],
    },
  ];

  return (
    <section className="bg-[rgb(11,21,25)] py-32 text-white">
      <div className="container mx-auto">
        <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center">
          <h2 className="font-serif text-2xl text-white md:text-4xl">
            {t.features_pages['From conversations to Jira tickets']}
          </h2>
          <p className="text-sm text-white/80 md:text-base">
            {
              t.features_pages[
                'AI agent understands root causes, clusters similar issues, and generates complete Jira tickets with customer impact analysis.'
              ]
            }
          </p>
        </div>
        <div className="mb-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 md:p-7"
            >
              <h3 className="font-serif text-lg text-white md:text-xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function CustomerInsightsPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.features_pages['Every customer touch,']}
            <br />
            {t.features_pages['one unified voice']}
          </>
        }
        subtitle={
          t.features_pages[
            'Customer Insights analyzes every interaction across all channels and turns raw conversations into actionable insights. What your customers actually want, delivered to the right teams, automatically.'
          ]
        }
      />

      <Logos />

      <BenefitsSection
        title={t.features_pages['Customer Insights Features']}
        subtitle={
          t.features_pages[
            'AI that understands what drives your support volume'
          ]
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="black"
        benefits={[
          {
            title: t.features_pages['Understands What Drives Support'],
            description:
              t.features_pages[
                "The AI agent analyzes every conversation to identify what's actually driving support requests, feature requests, and bug reports. Not just counting—understanding root causes."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/customer-insights/understanding-root-causes.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['Smart Clustering'],
            description:
              t.features_pages[
                'AI clusters similar issues together. 50 conversations about the same bug become one actionable cluster. See patterns, not individual tickets.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/customer-insights/smart-clustering.mp4',
            imagePosition: 'left',
          },
          {
            title: t.features_pages['Auto-Generated Jira Tickets'],
            description:
              t.features_pages[
                'Deep research agent generates complete Jira tickets for your team. Full context, customer impact analysis, affected user count, and priority level—all automated.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/customer-insights/auto-generated-jira-tickets.mp4',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['Team Competition & Gamification'],
            description:
              t.features_pages[
                "Teams compete to resolve insights. Leaderboards track who's solving the most impactful customer problems. Turn insights into team motivation."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/customer-insights/team-competition-and-gamification.mp4',
            imagePosition: 'left',
          },
        ]}
      />

      {/* Authentic Message Section */}
      <section className="overflow-hidden py-32">
        <div className="container w-full leading-relaxed">
          <h2 className="text-muted-foreground/40 mt-10 text-center font-serif text-3xl md:text-4xl">
            {isAr ? (
              <>
                {'تضيع '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Feedback"
                  className="px-1"
                >
                  ملاحظات
                </LinkPreview>{' '}
                العملاء بين التذاكر والإيميلات والمحادثات. مبعثرة بين القنوات،
                مدفونة في سلاسل الردود.
                <br />
                <br />
                رؤى العملاء تلتقط كل تفاعل — المكالمات، والدردشة، والإيميل،
                وWhatsApp، والسوشيال ميديا، وSlack — وتحوّل المحادثات الخام إلى
                رؤى واضحة وقابلة للتنفيذ تستفيد منها{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Product_manager"
                  className="px-1"
                >
                  فرق المنتج
                </LinkPreview>{' '}
                فعلاً.
                <br />
                <br />
                لا لوحات مكتظة بالأرقام. رؤى بلغة واضحة عمّا يريده عملاؤك فعلاً.
              </>
            ) : (
              <>
                Customer{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Feedback"
                  className="px-1"
                >
                  feedback
                </LinkPreview>{' '}
                gets lost in tickets, emails, and chats. Scattered across
                channels, buried in threads.
                <br />
                <br />
                Customer Insights captures every touch—Voice, Chat, Email,
                WhatsApp, Social, Slack—and turns raw conversations into clear,
                actionable insights that{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Product_manager"
                  className="px-1"
                >
                  product teams
                </LinkPreview>{' '}
                can actually use.
                <br />
                <br />
                Not dashboards full of numbers. Plain language insights about
                what customers actually want.
              </>
            )}
          </h2>
        </div>
      </section>

      {/* Capabilities Section - Dark */}
      <InsightsCapabilities />

      {/* API Section */}
      <section className="bg-white py-32">
        <div className="container">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-4 font-serif text-2xl md:text-4xl">
              {t.features_pages['API-First Customer Insights']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.features_pages[
                  'Access insights programmatically. Build custom dashboards, integrate with your tools, automate actions.'
                ]
              }
            </p>
          </div>
          <div className="mx-auto max-w-4xl space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Get Customer Insights']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Retrieve all customer insights with optional filtering'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  GET /insights
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Get Specific Insight']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Get detailed information about a specific insight'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  GET /insights/:id
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Mark as Resolved']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {
                    t.features_pages[
                      'Mark an insight as resolved when action is taken'
                    ]
                  }
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /insights/:id/resolve
                </code>
              </div>
              <div className="border-border group hover:border-primary rounded-lg border p-6 transition-colors">
                <h3 className="mb-2 text-lg">
                  {t.features_pages['Assign to Team']}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t.features_pages['Assign insight to a team member or group']}
                </p>
                <code className="bg-muted block rounded p-2 text-xs">
                  POST /insights/:id/assign
                </code>
              </div>
            </div>
            <div className="flex justify-center pt-8">
              <Button size="lg" asChild>
                <a
                  href="https://docs.open.cx/api-reference/customer-insights"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.features_pages['View Insights API Docs']}{' '}
                  <ArrowUpRight className="ms-2 size-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.features_pages[
            'Find answers to common questions about Customer Insights.'
          ]
        }
        items={[
          {
            question:
              t.features_pages['What channels are analyzed for insights?'],
            answer:
              t.features_pages[
                'All of them. Voice, Chat, Email, WhatsApp, Social (Instagram & Messenger), and Slack. Every customer interaction across every channel.'
              ],
          },
          {
            question: t.features_pages['How are insights presented?'],
            answer:
              t.features_pages[
                'Plain language summaries that anyone can understand. No data science degree required. Just clear, actionable insights about customer needs.'
              ],
          },
          {
            question: t.features_pages['What is the deep research agent?'],
            answer:
              t.features_pages[
                'One-click deep dive into any insight. See all conversations that contributed to it, understand the context, and analyze customer pain points in detail.'
              ],
          },
          {
            question: t.features_pages['How does auto-assignment work?'],
            answer:
              t.features_pages[
                'Insights are automatically assigned to relevant teams based on topic. Engineering gets technical issues, product gets feature requests, support gets service problems.'
              ],
          },
          {
            question: t.features_pages['What are weekly impact reminders?'],
            answer:
              t.features_pages[
                'Teams receive weekly emails about their assigned insights and their impact on customers. Keeps insights top-of-the-mind and teams accountable.'
              ],
          },
          {
            question: t.features_pages['How does gamification work?'],
            answer:
              t.features_pages[
                'Teams earn points for resolving insights. Leaderboards show top performers. Recognition for solving customer problems. Competition drives faster resolution.'
              ],
          },
          {
            question: t.features_pages['Can I access insights via API?'],
            answer:
              t.features_pages[
                'Yes. Full API access to get insights, mark as resolved, and assign to teams. Build custom dashboards or integrate with your existing tools.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
